import { useCallback, useEffect, useState } from "react";
import { dataApi, api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import TeamFormDialog from "@/components/TeamFormDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BranchTag, useBranches } from "@/components/BranchBits";

export default function Teams() {
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    dataApi.teams().then((r) => setTeams(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this team? Members will be un-assigned but not deleted.")) return;
    try { await api.delete(`/teams/${id}`); toast.success("Team deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rowActions = useCallback((r) => (
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="icon" onClick={() => nav(`/teams/${r.id}`)} data-testid={`view-team-${r.id}`}><Eye className="w-4 h-4" /></Button>
      {r.can_modify && <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`edit-team-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
      {r.can_modify && <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`del-team-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
    </div>
  ), [nav]);

  const columns = [
    { key: "name", label: "Team", sortable: true, render: (r) => (
      <div>
        <div className="font-medium">{r.name}</div>
        <div className="text-[10px] text-muted-foreground truncate max-w-[240px]">{r.description}</div>
      </div>
    )},
    { key: "department", label: "Department", sortable: true, render: (r) => <Badge variant="outline">{r.department}</Badge> },
    { key: "member_count", label: "Members", sortable: true, className: "number", render: (r) => (r.member_ids || []).length },
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "velocity", label: "Velocity", sortable: true, className: "number" },
    { key: "productivity", label: "Productivity", sortable: true, className: "number", render: (r) => `${r.productivity}%` },
    { key: "created_at", label: "Created", sortable: true, render: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        testid="teams-header" title="Teams" subtitle={`${teams.length} teams`}
        actions={canCreate && <Button data-testid="new-team-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-1" />New Team</Button>}
      />
      <DataTable
        testid="dt-team"
        loading={loading}
        empty="No teams in your scope"
        columns={columns}
        data={teams}
        searchKeys={["name", "department", "description"]}
        rowActions={rowActions}
      />
      <TeamFormDialog open={dialogOpen} onOpenChange={setDialogOpen} team={editing} onSaved={load} />
    </div>
  );
}
